import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NEXT_STATUS } from '../../components/ui/status';
import api from '../../services/api';
import type { Order, OrderStatus } from '../../types/Order';
import { OFFLINE_AFTER_FAILURES, POLL_MS } from './config';
import { isActive, PaymentChange, putPayment, putStatus, saveOrder } from './orderApi';

export type FeedState = 'live' | 'reconnecting' | 'offline';

/** What the toasts and menus need from a list of orders: the feed, or a single order's editor. */
export interface OrderOps {
    advance(id: string): Promise<void>;
    setPayment(id: string, p: PaymentChange): Promise<void>;
    cancel(id: string): Promise<void>;
    revert(id: string, to: OrderStatus): Promise<void>;
}

export interface OrdersFeed extends OrderOps {
    /** Active orders (not completed, not cancelled), server order. */
    orders: Order[];
    /** false until the first answer for this location. */
    loaded: boolean;
    state: FeedState;
    /** ms epoch of the last successful poll, 0 before the first. */
    lastUpdated: number;
    /** Ids that arrived after the first load. */
    newIds: ReadonlySet<string>;
    /** Ids with a save in flight. */
    saving: ReadonlySet<string>;
    refresh(): void;
}

const upsert = (list: Order[], order: Order): Order[] =>
    list.some((o) => o.id === order.id) ? list.map((o) => (o.id === order.id ? order : o)) : [...list, order];

const withId = (set: ReadonlySet<string>, id: string): Set<string> => new Set(set).add(id);

/**
 * Active orders of one location, polled every POLL_MS while the tab is visible.
 * Writes are optimistic and roll back on failure (the error is rethrown for the caller's toast).
 */
export function useOrdersFeed(locationId: string | null): OrdersFeed {
    const [all, setAll] = useState<Order[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [state, setState] = useState<FeedState>('live');
    const [lastUpdated, setLastUpdated] = useState(0);
    const [newIds, setNewIds] = useState<ReadonlySet<string>>(new Set());
    const [saving, setSaving] = useState<ReadonlySet<string>>(new Set());

    const current = useRef<Order[]>([]);
    const generation = useRef(0); // bumped when a write starts and ends; polls that overlap one are dropped
    const inflight = useRef(new Map<string, number>());
    const finished = useRef(new Map<string, Order>()); // orders that left the board, kept for undo
    const known = useRef<Set<string> | null>(null); // null until the first answer
    const run = useRef<() => Promise<void>>(async () => undefined);

    const commit = useCallback((next: Order[]) => {
        current.current = next;
        setAll(next);
    }, []);

    useEffect(() => {
        commit([]);
        setLoaded(false);
        setState('live');
        setLastUpdated(0);
        setNewIds(new Set());
        known.current = null;
        inflight.current.clear();
        finished.current.clear();
        let failures = 0;
        let fetching = false;
        let cancelled = false;

        const poll = async () => {
            if (fetching) return;
            fetching = true;
            const started = generation.current;
            try {
                const { data } = await api.get<Order[]>('/orders', {
                    params: { status: 'active', locationId: locationId ?? undefined },
                });
                if (cancelled) return;
                failures = 0;
                setState('live');
                setLastUpdated(Date.now());
                if (started !== generation.current) return; // a write happened meanwhile; the next poll is accurate
                const merged = data.map((o) => (inflight.current.has(o.id) ? (current.current.find((x) => x.id === o.id) ?? o) : o));
                commit(merged);
                setLoaded(true);
                if (known.current === null) {
                    known.current = new Set(merged.map((o) => o.id));
                } else {
                    const seen = known.current;
                    const arrived = merged.filter((o) => !seen.has(o.id)).map((o) => o.id);
                    arrived.forEach((id) => seen.add(id));
                    if (arrived.length > 0) setNewIds((prev) => new Set([...prev, ...arrived]));
                }
            } catch {
                if (cancelled) return;
                failures += 1;
                setState(failures >= OFFLINE_AFTER_FAILURES ? 'offline' : 'reconnecting');
            } finally {
                fetching = false;
            }
        };
        run.current = poll;

        void poll();
        const timer = window.setInterval(() => {
            if (!document.hidden) void poll();
        }, POLL_MS);
        const onVisible = () => {
            if (!document.hidden) void poll();
        };
        const onOnline = () => void poll();
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('online', onOnline);
        return () => {
            cancelled = true;
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('online', onOnline);
        };
    }, [locationId, commit]);

    const refresh = useCallback(() => void run.current(), []);

    const mutate = useCallback(
        async (id: string, patch: Partial<Order>, send: () => Promise<Order>) => {
            const before = current.current.find((o) => o.id === id) ?? finished.current.get(id);
            if (!before) return;
            const place = (order: Order) => {
                if (isActive(order.status)) {
                    finished.current.delete(id);
                    commit(upsert(current.current, order));
                } else {
                    finished.current.set(id, order);
                    commit(current.current.filter((o) => o.id !== id));
                }
            };
            generation.current += 1;
            inflight.current.set(id, (inflight.current.get(id) ?? 0) + 1);
            setSaving((s) => withId(s, id));
            place({ ...before, ...patch });
            try {
                const saved = await saveOrder(before, patch, send);
                // the answer carries the durations as the server counts them now
                const shown = current.current.find((o) => o.id === id);
                if (shown?.status === saved.status) place({ ...shown, timing: saved.timing });
            } catch (e) {
                place(before);
                throw e;
            } finally {
                generation.current += 1;
                const left = (inflight.current.get(id) ?? 1) - 1;
                if (left > 0) {
                    inflight.current.set(id, left);
                } else {
                    inflight.current.delete(id);
                    setSaving((s) => {
                        const next = new Set(s);
                        next.delete(id);
                        return next;
                    });
                }
            }
        },
        [commit]
    );

    const revert = useCallback(
        (id: string, to: OrderStatus) => mutate(id, { status: to }, () => putStatus(id, to)),
        [mutate]
    );
    const advance = useCallback(
        async (id: string) => {
            const order = current.current.find((o) => o.id === id);
            const next = order && NEXT_STATUS[order.status];
            if (next) await revert(id, next);
        },
        [revert]
    );
    const cancel = useCallback((id: string) => revert(id, 'cancelled'), [revert]);
    const setPayment = useCallback(
        (id: string, p: PaymentChange) =>
            mutate(id, { paymentStatus: p.status, ...(p.method ? { paymentMethod: p.method } : {}) }, () => putPayment(id, p)),
        [mutate]
    );

    const orders = useMemo(() => all.filter((o) => isActive(o.status)), [all]);

    return { orders, loaded, state, lastUpdated, newIds, saving, refresh, advance, setPayment, cancel, revert };
}
